/**
 * Config API service for feature flags and settings.
 *
 * Uses path traversal (../../config/v1) to escape the /api/v1 base URL,
 * same pattern as sandbox mode (../../sandbox) in api.config.ts.
 *
 * The config API is mounted at /config on the HTTP server (cmd/server/main.go:596),
 * separate from the main /api/v1 handler. Auth headers, token refresh, retry,
 * and org-id header are handled automatically by the shared apiClient.
 *
 * @see pkg/dynconfig/ for backend access rules
 * @see config-server/proto/config/v1/config.proto for API contract
 */

import { apiClient } from '@services/api.config';

import type { ConfigGetResponse, ConfigListResponse } from '@/types/config';

/** Path prefix to escape /api/v1 and reach /config/v1 */
const CONFIG_PREFIX = '/../../config/v1';

/**
 * Decode a config entry's base64-encoded value.
 *
 * The config-server stores values as bytes with a codec field.
 * In gRPC-Gateway JSON responses, bytes are base64-encoded.
 * This function decodes base64 → JSON string → parsed value.
 *
 * @param base64Value - The base64-encoded string from ConfigEntry.value
 * @param fallback - Value to return if decoding fails
 * @returns The decoded and parsed value, or fallback on error
 */
export function decodeConfigValue<T>(base64Value: string, fallback: T): T {
  try {
    const jsonStr = atob(base64Value);
    return JSON.parse(jsonStr) as T;
  } catch {
    return fallback;
  }
}

export const configService = {
  /**
   * List all keys in a namespace, optionally only those starting with `prefix`.
   * GET /config/v1/namespaces/{namespace}/keys[?prefix=...]
   */
  async listKeys(namespace: string, prefix?: string): Promise<ConfigListResponse> {
    const query = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
    const url = `${CONFIG_PREFIX}/namespaces/${encodeURIComponent(namespace)}/keys${query}`;
    const response = await apiClient.get<ConfigListResponse>(url);
    return response.data;
  },

  /**
   * Get a single key from a namespace.
   * GET /config/v1/namespaces/{namespace}/keys/{key}
   */
  async getKey(namespace: string, key: string): Promise<ConfigGetResponse> {
    const url = `${CONFIG_PREFIX}/namespaces/${encodeURIComponent(namespace)}/keys/${encodeURIComponent(key)}`;
    const response = await apiClient.get<ConfigGetResponse>(url);
    return response.data;
  },
};
